import React, { useEffect } from 'react';
import {
  EVBox,
  EVDialog,
  EVTypography,
  EVButton,
  EVCircularProgress,
  EVIconButton,
  EVClear,
  EVDivider,
  EVDialogActions,
} from '@eagleview/ev-comp-library';
import { useSelector, useDispatch } from 'react-redux';
import { bool, func, number } from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  automatedSummaryBuilderAction,
  updateUserSelectionForAnomalyDetectionAction,
  setRunAutomatedSummaryBuilderWithOnOptionAction,
} from 'layout/adjuster/Adjuster.actions';
import { AUTOMATED_SUMMARY_BUILDER } from 'layout/adjuster/Adjuster.constants';
import { get } from 'lodash';
import useStyles from './AutomatedSummaryBuilder.styles';

const AutomatedSummaryBuilderModal = ({
  open, handleClose, orderId, isAutomatedSummaryMenuClicked,
}) => {
  const styles = useStyles();

  // actions
  const dispatch = useDispatch();
  const automatedSummaryBuilder = (payload) => dispatch(automatedSummaryBuilderAction(payload));
  const updateUserSelectionForAnomalyDetection = (payload) => dispatch(updateUserSelectionForAnomalyDetectionAction(payload));
  const setRunAutomatedSummaryBuilderWithOnOption = (payload) => dispatch(setRunAutomatedSummaryBuilderWithOnOptionAction(payload));

  // selectors
  const apiSuccess = useSelector((state) => get(state, 'adjusterReducer.apiSuccess', {}));
  const loading = useSelector((state) => get(state, 'adjusterReducer.loading', {}));
  const isAutomatedBuildSuccess = apiSuccess[AUTOMATED_SUMMARY_BUILDER];
  const isAutomatedBuildAPIInProgress = loading[AUTOMATED_SUMMARY_BUILDER];

  const updateSelectionFlag = (userSelection, updatedValue) => {
    if (!(isAutomatedSummaryMenuClicked && updatedValue === 'no')) {
      updateUserSelectionForAnomalyDetection({ orderId, userSelection, selectedValue: updatedValue });
    }
    handleClose();
  };

  const handleConfirm = () => {
    automatedSummaryBuilder({ orderId });
  };

  const handleCancel = () => {
    updateSelectionFlag(false, 'no');
  };

  useEffect(() => {
    setRunAutomatedSummaryBuilderWithOnOption(false);
  }, []);

  useEffect(() => {
    if (isAutomatedBuildSuccess) {
      updateSelectionFlag(true, 'yes');
    }
  }, [isAutomatedBuildSuccess]);

  const { t } = useTranslation();
  return (
    <EVDialog data-testid="automated-summary-dialog" open={open} onClose={handleCancel}>
      <EVBox
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        width="470px"
        height="320px"
      >
        <EVBox className={styles.alignContent} pt={1}>
          <EVBox className={styles.title} display="flex" justifyContent="space-between" alignItems="center">
            <EVTypography className={styles.titleText}>{t('adjusterMenu.runSummaryBuilder')}</EVTypography>
            <EVIconButton
              onClick={handleCancel}
              disabled={isAutomatedBuildAPIInProgress}
              data-testid="close-button"
              size="small"
            >
              <EVClear />
            </EVIconButton>
          </EVBox>
        </EVBox>
        <EVDivider />
        <EVBox className={styles.alignContent} pt={2}>
          <EVTypography className={styles.ConfirmationText}>{t('automatedSummaryBuilder.confirmation.body1')}</EVTypography>
          <EVTypography className={styles.ConfirmationText}>{t('automatedSummaryBuilder.confirmation.body2')}</EVTypography>
          <EVTypography className={styles.ConfirmationText}>{t('automatedSummaryBuilder.confirmation.body3')}</EVTypography>
        </EVBox>
        <EVDialogActions className={styles.actionButtons}>
          <EVButton
            data-testid="cancel-button"
            value="cancel"
            onClick={handleCancel}
            disabled={isAutomatedBuildAPIInProgress}
            className={styles.cancelButton}
          >
            {t('cancel')}
          </EVButton>
          <EVButton
            data-testid="submit-button"
            variant="contained"
            onClick={handleConfirm}
            color="primary"
            disabled={isAutomatedBuildAPIInProgress}
            className={styles.ConfirmButton}
          >
            {t('adjusterMenu.autoConfirmButton')}
            {isAutomatedBuildAPIInProgress && <EVCircularProgress size={20} />}
          </EVButton>
        </EVDialogActions>
      </EVBox>
    </EVDialog>
  );
};

AutomatedSummaryBuilderModal.propTypes = {
  open: bool.isRequired,
  handleClose: func.isRequired,
  orderId: number.isRequired,
  isAutomatedSummaryMenuClicked: bool.isRequired,
};

export default AutomatedSummaryBuilderModal;
